import React, {useState, useEffect} from 'react';
import emailjs from '@emailjs/browser';
import {config} from '../config.ts';
import ReactDOM from "react-dom/client";
import {renderTextField, renderTextArea, renderEmailField, renderDropdown} from "./fields/FormFields.tsx";
import {setElementAttribute} from "../utils/common-utils.ts";

export const renderFormFeedbackFr = (div: string) => {
    const root = document.getElementById(div)
    if (root) {
        ReactDOM.createRoot(root).render(<React.StrictMode><FormFeedbackFr/></React.StrictMode>)
    }
}
const FormFeedbackFr = () => {
    const [name, setName] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [issueType, setIssueType] = useState('select');
    const [otherIssue, setOtherIssue] = useState('');
    const [subject, setSubject] = useState('');
    const [feedback, setFeedback] = useState('');
    const [messageSent, setMessageSent] = useState(false);
    const [messageError, setMessageError] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
      if (messageSent) {
        document.getElementById("component-form-accessibility-fr")?.focus();
      }
    }, [messageSent]);

    const renderName = () => {
        const inputData = {
            name: 'name',
            id: 'nom',
            autoComplete: 'name',
            label: 'Nom',
            type: 'text',
            inputMode: 'text',
            value: '',
            required: false,
            description: 'field_description_name',
            message: 'Si vous souhaitez fournir de la rétroaction à titre anonyme, laissez le champ de noms vide.',
            errorMessageID: '',
            visible: true
        }
        return renderTextField(inputData, name, setName, submitted);
    }

    const renderEmailDetails = () => {
        const inputData = {
            name: 'email',
            id: 'email',
            label: 'Courriel',
            type: 'email',
            autoComplete: 'email',
            required: false,
            description: 'field_description_email',
            message: 'Si vous souhaitez fournir de la rétroaction à titre anonyme, veuillez laisser le champ d\'adresse courriel vide.',
            errorMessageID: '',
            visible: true,
            success: 'Merci pour votre adresse courriel.'
        }
        return renderEmailField(inputData, emailAddress, setEmailAddress, submitted);
    }

    const renderPhone = () => {
        const inputData = {
            name: 'phone',
            id: 'phone',
            label: 'Numéro de téléphone',
            type: 'text',
            autoComplete: 'tel-national',
            inputMode: 'tel',
            value: '',
            required: false,
            description: 'field_description_phone',
            message: 'Si vous souhaitez fournir de la rétroaction à titre anonyme, laissez le champ de numéro de téléphone vide.',
            errorMessageID: '',
            visible: true
        }
        return renderTextField(inputData, phone, setPhone, submitted);
    }

    const renderIssueType = () => {
        const inputData = {
            name: 'issue-type',
            id: 'issue-type',
            label: 'Type de problème',
            option: [
                {label: '- Choisir -', value: 'select'},
                {label: 'Accessibilité du contenu', value: 'Accessibilité du contenu'},
                {label: 'Accessibilité numérique (site web/application)', value: 'Accessibilité numérique (site web/application)'},
                {label: 'Emploi', value: 'Emploi'},
                {label: 'Installations de CPAC', value: 'Installations de CPAC'},
                {label: 'Demande de format différent', value: 'Demande de format différent'},
                {label: 'Autre (veuillez préciser)', value: 'Autre'},
            ],
            required: true,
            description: 'field_description_issue',
            message: 'Le type de problème est obligatoire. Veuillez choisir l\'option qui correspond le mieux à votre problème.',
            errorMessageID: 'issue_type_error_message',
            visible: true
        };

        return renderDropdown(inputData, issueType, setIssueType, submitted);
    }

    const renderOtherIssue = () => {
        const inputData = {
            name: 'other-issue',
            id: 'otherIssue',
            label: 'Type de problème - Autre',
            type: 'text',
            inputMode: 'text',
            autoComplete: 'off',
            value: '',
            required: false,
            description: 'field_description_other_issue',
            message: '',
            errorMessageID: '',
            visible: true
        }
        return renderTextField(inputData, otherIssue, setOtherIssue, submitted);
    }

    const renderSubject = () => {
        const inputData = {
            name: 'subject',
            id: 'subject',
            label: 'Objet',
            type: 'text',
            inputMode: 'text',
            autoComplete: 'off',
            value: '',
            required: true,
            description: 'field_description_subject',
            message: 'Le sujet est obligatoire. Veuillez résumer votre problème en moins de 60 caractères.',
            errorMessageID: 'subject_error_message',
            visible: true
        }
        return renderTextField(inputData, subject, setSubject, submitted);
    }

    const renderFeedback = () => {
        const inputData = {
            name: 'feedback',
            id: 'feedback',
            label: 'Commentaires',
            required: true,
            inputMode: 'text',
            description: 'field_description_feedback',
            message: 'Vos commentaires sont requis. Veuillez décrire votre problème, en précisant les détails pertinents tels que la page web ou l\'émission concernée. Les commentaires peuvent être d\'ordre général.',
            errorMessageID: 'feedback_error_message',
            visible: true
        }
        return renderTextArea(inputData, feedback, setFeedback, submitted);
    }

    const handleSubmit = (e: any) => {
        e.preventDefault();
        setSubmitted(true);
        const hasErrors = !issueType || issueType === 'select' || issueType === '' || !subject || !feedback ? true : false;

        const templateParams = {
            senderLanguage: "Français",
            senderName: name ? name : "anonyme",
            senderEmail: emailAddress ? emailAddress : "l'adresse courriel n'a pas été fournie",
            senderPhone: phone ? phone : "le numéro de téléphone n'a pas été fourni",
            senderIssueType: issueType === "Autre" ? otherIssue : issueType !== "Autre" ? issueType : "un problème n'a pas été fourni",
            senderSubject: subject ? subject : "aucun sujet n'a été fourni",
            senderComments: feedback
        };

        const errorMessages = document.querySelector("#error_summary") as HTMLElement | null;

        if (hasErrors) {    
            setElementAttribute("error_summary", "aria-labelledby", "error_summary_heading");
            document.getElementById("error_summary")?.focus();
        } else {
            emailjs.send('outlook_service', 'feedback_template', templateParams, config.emailjsKey)
            .then(
                function () {
                setMessageSent(true);
                e.target.reset(); // reset the form
                setSubmitted(false);
                errorMessages?.removeAttribute("aria-labelledby");
            }, 
            function () {
                setMessageError(true);
                document.getElementById("message_error")?.focus();
            });
        }
    }

    return (
        <>
            {
                messageSent ? <h2 id='message_success' className='form_wrapper message_success' role='status'>Nous vous remercions de votre
                    commentaire.</h2> : messageError ?
                    <h2 id='message_error' className='form_wrapper message_fail' role='alert'>Désolé, il y a eu un problème avec votre envoi. Prière de
                        réessayer plus tard.</h2> :
                    <div id="form_wrapper" className="form_wrapper">
                        <div id='error_summary' aria-live="assertive" tabIndex={-1}>
                            {submitted && (!issueType || issueType === 'select' || issueType === '' || !subject || !feedback) ? <h2 id='error_summary_heading' role='alert' className='error_message invalid'>Il y a des erreurs dans le formulaire.<br/><span>Veuillez corriger les erreurs et soumettre à nouveau.</span></h2> : null}
                            <ul className="reg-list">
                            {submitted && (!issueType || issueType === 'select' || issueType === '') ? <li id="issue_type_error_message" className='error_message invalid'><a href="#issue-type"><img alt="" className="error-icon" src="/static-assets/images/common/error.svg" /> Veuillez sélectionner un type de problème.</a></li> : null}
                            {submitted && !subject ? <li id="subject_error_message" className='error_message invalid'><a href="#subject"><img alt="" className="error-icon" src="/static-assets/images/common/error.svg" /> Veuillez entrer un sujet.</a></li> : null}
                            {submitted && !feedback ? <li id="feedback_error_message" className='error_message invalid'><a href="#feedback"><img alt="" className="error-icon" src="/static-assets/images/common/error.svg" /> Veuillez nous faire part de vos commentaires.</a></li> : null}
                            </ul>
                        </div>
                        <form aria-label="Formulaire de commentaires sur l'accessibilité" id="contact-form" onSubmit={(e) => handleSubmit(e)} itemScope
                              itemType="https://schema.org/WebPageElement" noValidate>
                            <div className="form_body" itemProp='mainEntityOfPage'>
                                {renderName()}
                                {renderEmailDetails()}
                                {renderPhone()}
                                {renderIssueType()}
                                {issueType === 'Autre' ? renderOtherIssue() : ''}
                                {renderSubject()}
                                {renderFeedback()}
                            </div>
                            <div className='form_footer left_label'>
                                <button type="submit" name='submit' value='soumettre' className="btn--primary">Soumettre</button>
                            </div>
                        </form>
                    </div>
            }
        </>
    )
}

export default FormFeedbackFr;